/**
 * "Showing results for X — search instead for Y." (item 21)
 *
 * The seam repairs a misspelled word before it searches, which is the only way
 * a single-word typo can return anything at all: `qualifcation` matches zero
 * documents, and no amount of reranking rescues a word the archive has never
 * written down. But a search that quietly answers a different question from the
 * one that was asked is worse than one that finds nothing, so the repair is
 * never silent and never final. This line is the "never silent" half.
 *
 * The undo link is the other half, and it is bound to the query it undoes:
 * `?correct=<the literal text>` tells the seam to leave THIS query alone. A
 * bare on/off flag would sit in the URL and silently disable correction for
 * every later query the user typed — stale state with no visible cause — so
 * instead it stops matching the moment they search for something else. See
 * backend `search/spelling.wanted`.
 *
 * Renders nothing at all unless a word was actually changed; `search_meta`
 * carries `corrected` only in that case.
 */
import React from 'react';
import PropTypes from 'prop-types';

import useSearchMeta from '../../helpers/useSearchMeta';
import useSearchControls from '../../helpers/useSearchControls';

const SearchCorrection = ({ block }) => {
  const { meta } = useSearchMeta(block);
  const [, setControls] = useSearchControls();

  const corrected = meta?.corrected;
  const original = meta?.corrected_from;
  if (!corrected || !original) return null;

  // The seam tells us what to call the param rather than the UI hardcoding it,
  // for the same reason every other control here reads its name from the
  // registry: one spelling, one owner.
  const param = meta?.correct_off_param || 'correct';

  return (
    <div className="pp-correction" role="status">
      <span className="pp-correction__shown">
        Showing results for <strong>{corrected}</strong>
      </span>
      <button
        type="button"
        className="pp-correction__undo"
        onClick={() => setControls({ [param]: original })}
      >
        Search instead for <em>{original}</em>
      </button>
    </div>
  );
};

SearchCorrection.propTypes = {
  /** Block id, so the correction is read from THIS block's subrequest. */
  block: PropTypes.string,
};

export default SearchCorrection;
